import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, TileLayer, Polygon, Marker, useMap, ImageOverlay } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { Box, Divider, IconButton, SvgIcon, useTheme, Typography } from '@mui/material';
import { convertStringToShape, convertStringToPoint, isPolygonInside, getCentroidFromShapeWKT } from '../../utils/utils';
import { AppTheme } from '../../theme';
import IconLib from '../../lib/icons/IconLib';
import PentagonOutlinedIcon from '@mui/icons-material/PentagonOutlined';

import { Trans } from 'react-i18next';
import { createMapStyles } from './MapsStyles';
import { useSharedContext } from '../../contexts/SharedContext';
import { t } from 'i18next';
import ConfirmationDialog from '../../lib/components/ConfirmationDialog/ConfirmationDialog';
import { ButtonApp } from '../../lib/commonStyles';

export interface indexImageOverlayProps {
    image: string;
    bbox: L.LatLngBoundsExpression;
}


const GeoJsonLayer = ({ geoJsonList }: { geoJsonList: any[]; }) => {
    const map = useMap();
    const geoJsonRefs = useRef<any[]>([]);

    useEffect(() => {
        // Eliminar capas anteriores si existen
        geoJsonRefs.current.forEach(layer => {
            map.removeLayer(layer);
        });
        geoJsonRefs.current = [];

        // Crear y agregar nuevas capas
        if (Array.isArray(geoJsonList)) {
            geoJsonList.forEach((geoJson) => {
                const newGeoJsonLayer = L.geoJSON(geoJson, {
                    style: (feature) => ({
                        fillColor: feature?.properties?.color || '#ffffff',
                        weight: 1,
                        opacity: 0.2,
                        color: 'white',
                        fillOpacity: 1
                    })
                });
                newGeoJsonLayer.addTo(map);
                geoJsonRefs.current.push(newGeoJsonLayer);
            });
        }
    }, [geoJsonList, map]);

    // Limpieza al desmontar
    useEffect(() => {
        return () => {
            geoJsonRefs.current.forEach(layer => {
                map.removeLayer(layer);
            });
            geoJsonRefs.current = [];
        };
    }, [map]);

    return null;
};


export interface TiffProps {
    blob: Blob | null;
}

interface TileProps {
    url: string;
    attribution: string;
    subdomains: string[];
};

interface Props {
    shape: string;
    location: string;
    showLocation?: boolean;
    indexImageOverlay?: indexImageOverlayProps;
    onPolygonEdit?: (newShape: string | null, newLocation: string | null) => Promise<void>;
    geoJsonList?: any[];
    onClickEditButton?: () => void;
    miniMapImage?: indexImageOverlayProps;
    canEditPolygon?: boolean;
    mode?: "edit" | "create";
}

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
});

// Componente para manejar el auto-centrado y resize del mapa
const MapCenterHandler = ({ containerRef, shapeCoordinates, center, isEditing }: { containerRef: React.RefObject<HTMLDivElement | null>, shapeCoordinates: L.LatLngExpression[][][], center?: L.LatLngExpression, isEditing: boolean }) => {
    const map = useMap();
    // --- Resize handling
    useEffect(() => {
        if (!containerRef.current || !map || isEditing) return;

        const observer = new ResizeObserver(() => {
            // recalcular tamaño del mapa
            map.invalidateSize();

            // si hay shape ajustamos
            if (shapeCoordinates.length > 0 && shapeCoordinates[0].length > 0 && shapeCoordinates[0][0].length > 0) {
                const bounds = L.polygon(shapeCoordinates).getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] });
                }
            }
        });

        observer.observe(containerRef.current);
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [containerRef, map]);

    // TODO: Revisar centrado al ingresar en modo edición
    // --- Centralized recentering logic
    useEffect(() => {
        if (!map || isEditing) return;
        // recalcular tamaño del mapa
        map.invalidateSize();

        // si hay polígonos
        if (shapeCoordinates.length > 0 && shapeCoordinates[0].length > 0 && shapeCoordinates[0][0].length > 0) {
            const bounds = L.polygon(shapeCoordinates).getBounds();
            if (bounds.isValid()) {
                map.fitBounds(bounds, { padding: [20, 20] });
            }
        // si hay un punto/centro definido
        } else if (center) {
            map.setView(center, 14);
        // vista global
        } else {
            map.setView([0, 0], 2);
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, shapeCoordinates, center]);

    return null;
};

const CustomZoomControl = ({shapeCoordinates, location}:{shapeCoordinates:L.LatLngExpression[][][], location: L.LatLngExpression | undefined}) => {
    const theme: AppTheme = useTheme();
    const map = useMap();
    const [zoom, setZoom] = useState(map.getZoom());
    const [centerMode, setCenterMode] = useState<"user" | "shape">("shape");

    const flyTo = () => {
        if (centerMode === "shape") {
            centerUser();
        } else {
            centerShape();
        }
    };

    const centerUser = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    map.flyTo([position.coords.latitude, position.coords.longitude], 15);
                    setCenterMode("user");
                },
                (error) => {
                    // Si falla, fallback al shape
                    centerShape();
                }
            );
        }
    }
    const centerShape = () => {
        if (shapeCoordinates.length > 0 && shapeCoordinates[0].length > 0 && shapeCoordinates[0][0].length) {
            const bounds = L.polygon(shapeCoordinates).getBounds();
            if (bounds.isValid()) {
                map.flyToBounds(bounds, { padding: [20, 20] });
            }
            setCenterMode("shape");
        } else if (location) {
            map.flyTo(location, 14);
            setCenterMode("shape");
        } else {
            console.log("No hay shape para centrar");
            centerUser();
        }
    };

    const updateZoom = (value:number) => {
        const newZoom = Math.max(map.getZoom() + value,0)
        map.setZoom(newZoom);
        setZoom(newZoom);
    }

    useEffect(() => {
        const onZoomEnd = () => setZoom(map.getZoom());
        map.on('zoomend', onZoomEnd);
        return () => {
            map.off('zoomend', onZoomEnd);
        }
    }, [map])

    return (
        <>
            <Box sx={{ display: "flex", flexDirection: "column", backgroundColor: theme.palette.neutral_5, position: "absolute", zIndex: 999, top: 20, right: 16, borderRadius:"8px" }} >
                <IconButton onClick={() => updateZoom(1)} sx={{ p: "8px" }} disabled={zoom === 18}>
                    <SvgIcon inheritViewBox component={IconLib.plusSvg} title={<Trans>MAP.ZOOM_IN</Trans>} sx={{ fontSize: "24px", color: zoom === 18 ? theme.palette.neutral_3 :theme.palette.neutral_1}}/>
                </IconButton>
                <Divider sx={{ borderBottomWidth: 1, backgroundColor: theme.palette.neutral_3 }}/>
                <IconButton onClick={() => updateZoom(-1)} sx={{ p: "8px" }} disabled={zoom === 0}>
                    <SvgIcon inheritViewBox component={IconLib.minusSvg} title={<Trans>MAP.ZOOM_OUT</Trans>} sx={{ fontSize: "24px", color: zoom === 0 ? theme.palette.neutral_3 :theme.palette.neutral_1 }} />
                </IconButton>
                <Divider sx={{ borderBottomWidth: 1, backgroundColor: theme.palette.neutral_3 }} />
                <IconButton onClick={flyTo} sx={{ p: "8px" }}>
                    <SvgIcon inheritViewBox component={IconLib.aimSvg} title={<Trans>MAP.GEOLOCATION</Trans>} sx={{ fontSize: "24px", color:theme.palette.neutral_1 }} />
                </IconButton>
            </Box>
        </>
    )
}

const SetMapRef: React.FC<{ mapRef: React.RefObject<L.Map | null> }> = ({ mapRef }) => {
    const map = useMap();
    useEffect(() => {
        mapRef.current = map;
    }, [map, mapRef]);
    return null;
};

interface MiniMapProps {
    indexImageOverlay?: indexImageOverlayProps;
    tileProps: TileProps;
}


const MiniMap = ({ indexImageOverlay, tileProps } : MiniMapProps) => {
    if (!indexImageOverlay) return null;
    // Calcula el centro del bbox
    const bbox = indexImageOverlay.bbox as [number, number][];
    const center = [
        (bbox[0][0] + bbox[1][0]) / 2,
        (bbox[0][1] + bbox[1][1]) / 2
    ];
    return (
        <Box sx={{
            position: 'absolute',
            top: 66,
            right: 60,
            zIndex: 900,
            width: 160,
            height: 120,
            border: '2px solid #fff',
            borderRadius: 2,
            boxShadow: 3,
            overflow: 'hidden',
            background: '#eee'
        }}>
            <MapContainer
                center={center as [number, number]}
                zoom={14}
                style={{ width: '100%', height: '100%' }}
                zoomControl={false}
                attributionControl={false}
            >
                <TileLayer
                    {...tileProps}
                />
                <ImageOverlay
                    url={`data:image/png;base64,${indexImageOverlay.image}`}
                    bounds={indexImageOverlay.bbox}
                />
            </MapContainer>
        </Box>
    );
};


export default function Map({ shape, location, showLocation, indexImageOverlay, onPolygonEdit, onClickEditButton, geoJsonList, miniMapImage, canEditPolygon, mode= "edit" }: Props) {
    const [shapeCoordinates, setShapeCoordinates] = useState<L.LatLngExpression[][][]>([[]])
    const [center, setCenter] = useState<L.LatLngExpression>()
    // Estado para el marcador de location en edición
    const mapRef = useRef<L.Map>(null);
    const featureGroupRef = useRef<L.FeatureGroup | null>(null);
    const polygonRef = useRef<L.Polygon | null>(null);
    const drawPolygonControlRef = useRef<any>(null); // Referencia al control de dibujo
    const drawMarkerControlRef = useRef<any>(null); // Referencia al control de dibujo de marcador
    // Referencia al contenedor del mapa para manejo de resize
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDrawingMarker, setIsDrawingMarker] = useState(false);
    const [originalShape, setOriginalShape] = useState<L.LatLngExpression[][][]>([[]]);
    const [originalCenter, setOriginalCenter] = useState<L.LatLngExpression>()
    const [originalShapeBeforeDelete, setOriginalShapeBeforeDelete] = useState<L.LatLngExpression[][][]>([[]]);
    const originalAllEditablePolygonsRef = useRef<any[]>([]);
    const [confirmEdit, setConfirmEdit] = useState(false);
    const [cancelEdit, setCancelEdit] = useState(false);
    const [editNewShape, setEditNewShape] = useState<string | null>(null);
    const [editNewLocation, setEditNewLocation] = useState<string | null>(null);
    const canConfirmEdit = useMemo(() => !!editNewShape && !!editNewLocation, [editNewShape, editNewLocation]);
    const tileProps: TileProps = {
        url: 'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        attribution: '&copy; <a href="https://www.google.com/maps/">Google Maps</a>',
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    }
    const theme: AppTheme = useTheme();
    const styles = createMapStyles(theme);
    
    // Contexto compartido
    const {sharedData, setSharedData} = useSharedContext();

    useEffect(() => {
        if (sharedData.mapViewerPage.isEditing && !sharedData.mapViewerPage.isDrawing) handleEditPolygon();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[sharedData.mapViewerPage.isEditing])

    useEffect(() => {
        if (sharedData.mapViewerPage.isDrawing) {
            handleDrawPolygon();
            if(!sharedData.mapViewerPage.isEditing) handleEditPolygon();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sharedData.mapViewerPage.isDrawing]);

    // Efecto para manejar el modo de definición de location con Leaflet Draw
    useEffect(() => {
        if (isDrawingMarker) {
            handleDrawMarker();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDrawingMarker]);

    // Asegura que al desmontar el componente, el modo edición se desactive
    useEffect(() => {
        return () => {
            setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, mapEditMode: false, isEditing: false, isDrawing: false} }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    
    // Inicializa el FeatureGroup solo una vez
    useEffect(() => {
        setTimeout(() => {
            const map = mapRef.current;
            if (!map) return;

            if (!featureGroupRef.current) {
                featureGroupRef.current = new L.FeatureGroup();
                (featureGroupRef.current as any)._leaflet_id = 'drawnItems';
                map.addLayer(featureGroupRef.current);
            }
            // Guardar referencia local para cleanup
            const cleanupMap = map;
            return () => {
                if (featureGroupRef.current && cleanupMap) {
                    cleanupMap.removeLayer(featureGroupRef.current);
                    featureGroupRef.current = null;
                }
            }
        }, 0);
    }, []);

    // Sincroniza el polígono con el estado/props
    useEffect(() => {
        // Si estamos en modo edición, no sincronizar para evitar perder las opciones de edición
        // TODO: Revisar centrado al ingresar en modo edición
        if (sharedData.mapViewerPage.isEditing) {
            return;
        }
        
        if (!featureGroupRef.current) {
            return;
        }
        
        featureGroupRef.current.clearLayers();
        
        const coords = shapeCoordinates;
        if (coords.length > 0 && coords[0].length > 0) {
            console.log("Coordenadas del polígono:", coords);
            // Configuración del polígono con opciones mejoradas para edición
            const poly = L.polygon(coords as L.LatLngExpression[][][], { 
                color: 'red',
                fillRule: 'evenodd', // Importante para renderizar correctamente los anillos interiores
                fillOpacity: sharedData.mapViewerPage.isEditing ? 0.5 : 0,
                interactive: true, // Asegura que el polígono sea interactivo
                weight: 3 // Borde más grueso para facilitar la selección
            });
            
            // Nos aseguramos de que la instancia de edición esté disponible
            if (poly && typeof (L.Polygon.prototype as any).editing !== 'undefined') {
                (poly as any)._initHooks = true; // Marca el polígono como inicializado
            }
            
            featureGroupRef.current.addLayer(poly);
            polygonRef.current = poly;

        } else {
            polygonRef.current = null;
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shapeCoordinates, sharedData.mapViewerPage.isEditing]);

    useEffect(() => {
        console.log("Inicializando coordenadas y centro del mapa", shape, location);
        if (!shape && location) {
            setCenter(convertStringToPoint(location as string)[0]);
            setShapeCoordinates([[]]);
            return;
        }
        if (shape && !location) {
            console.log("No hay coordenadas de ubicación, pero hay un shape:", shape);
            setShapeCoordinates(convertStringToShape(shape as string));
            return;
        }
        setShapeCoordinates(convertStringToShape(shape as string));
        setCenter(convertStringToPoint(location as string)[0]);
    }, [shape, location]);

    // Agregar estilos personalizados para los tooltips
    useEffect(() => {
        const style = document.createElement('style');
        // box-shadow: ${theme.shadows[2]} !important;
        // border: 1px solid ${theme.palette.neutral_3} !important;
        style.innerHTML = `
        .leaflet-draw-tooltip {
            background-color: ${theme.palette.neutral_4} !important;
            color: ${theme.palette.neutral_1} !important;
            border-radius: 8px !important;
            padding: 8px 16px !important;
        }

        .leaflet-draw-tooltip::before {
            border-right-color: ${theme.palette.neutral_4} !important;
        }
        
        .leaflet-draw-tooltip-single {
            background-color: ${theme.palette.neutral_4} !important;
            color: ${theme.palette.neutral_1} !important;
        }
        
        .leaflet-draw-tooltip-subtext {
            color: ${theme.palette.tertiary.main} !important;
        }
        
        .leaflet-draw-tooltip-subtext > br {
            display: none !important;
        }
    `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, [theme]);

    // Función para activar edición desde el botón
    const handleEditPolygon = () => {
        onClickEditButton?.();
        setOriginalShape(shapeCoordinates);
        setOriginalCenter(center);
        setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, mapEditMode: true} }));
        if(!sharedData.mapViewerPage.isEditing) setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, isEditing: true} }));
        const map = mapRef.current;
        if (!map) return;

        if (!center) {
            if (!shapeCoordinates || shapeCoordinates.length === 0 || shapeCoordinates[0].length === 0 || shapeCoordinates[0][0].length === 0) {
                // Obtener el centro actual de su posición o del mapa y setearlo como centro
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const coords: L.LatLngExpression = [position.coords.latitude, position.coords.longitude];
                            map.setView(coords, 13);
                        },
                        (error) => {
                            console.log("Error obteniendo geolocalización:", error);
                            const coords: L.LatLngExpression = [map.getCenter().lat, map.getCenter().lng];
                            map.setView(coords);
                        }
                    )
                } else {
                    console.log("Geolocalización no soportada en este navegador");
                    const coords: L.LatLngExpression = [map.getCenter().lat, map.getCenter().lng];
                    map.setView(coords);
                }
            } else {
                // Si ya hay shape, usar el centro de esas shape
                const bounds = L.polygon(shapeCoordinates).getBounds();
                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] });
                    setCenter(bounds.getCenter());
                }
            }
        }
        
        if (!polygonRef.current) {
            polygonRef.current = L.polygon(shapeCoordinates as L.LatLngExpression[][][], {
                color: 'red',
                fillRule: 'evenodd',
                fillOpacity: 0.5,
                interactive: true,
                weight: 3
            });
        }

        // Deshabilitamos cualquier edición previa si existe
        if ((polygonRef.current as any).editing && (polygonRef.current as any).editing._enabled) {
            (polygonRef.current as any).editing.disable();
        }
        
        if (!featureGroupRef.current) return;

        // Limpiamos el feature group
        featureGroupRef.current.clearLayers();
        
        // Copia profunda para manipulación
        const currentCoordinates = JSON.parse(JSON.stringify(shapeCoordinates));
        
        // Inicializar array para almacenar todos los polígonos editables
        const allEditablePolygons: any[] = [];
        
        for (let polygonIndex = 0; polygonIndex < currentCoordinates.length; polygonIndex++) {
            const polygonCoords = currentCoordinates[polygonIndex];
            
            if (!polygonCoords || polygonCoords.length === 0) continue;
            
            // Para polígonos con agujeros, necesitamos un enfoque que permita editar
            // directamente los vértices de los agujeros
            if (polygonCoords.length > 1) {                        // Extraer el anillo exterior
                // Primero creamos un polígono principal que muestre el conjunto completo
                const mainPolygon = L.polygon(polygonCoords, {
                    color: 'red',
                    fillRule: 'evenodd',
                    fillOpacity: 0.5,
                    interactive: true,
                    weight: 3
                }); // Añadimos el polígono principal al grupo
                featureGroupRef.current.addLayer(mainPolygon);
                
                // Guardar referencia al polígono editable
                if (polygonIndex === 0) {
                    polygonRef.current = mainPolygon;
                }
                
                // Guardar referencia a todos los polígonos editables
                allEditablePolygons.push({
                    index: polygonIndex,
                    polygon: mainPolygon,
                    type: 'main'
                });
                
                // Ahora, para cada agujero, crearemos polígonos editables separados
                // que estarán sincronizados con el polígono principal
                (mainPolygon as any)._holePolygons = [];
                
                // Para cada agujero...
                for (let i = 1; i < polygonCoords.length; i++) {
                    const hole = polygonCoords[i];
                    
                    // Crear un polígono editable para el agujero
                    const holePolygon = L.polygon(hole, {
                        color: 'blue',
                        weight: 2,
                        fillColor: 'blue',
                        fillOpacity: 0.2,
                        dashArray: '5, 5',
                        interactive: true // Debe ser interactivo para permitir la edición
                    });
                
                    // Lo añadimos al feature group para que sea editable
                    featureGroupRef.current.addLayer(holePolygon);
                    // Habilitar edición en agujero inmediatamente
                    if ((holePolygon as any).editing) {
                        (holePolygon as any).editing.enable();
                    }
                    // Guardar referencia para actualizaciones posteriores
                    (mainPolygon as any)._holePolygons.push({
                        index: i,
                        polygon: holePolygon
                    });
                }
                
                // Activamos la edición estándar del polígono principal
                if (mainPolygon && (mainPolygon as any).editing) {
                    (mainPolygon as any).editing.enable();
                }
            
            } else {
                // Para polígonos simples, usamos la aproximación estándar
                const simplePolygon = L.polygon(polygonCoords, {
                    color: 'red',
                    fillOpacity: 0.5,
                    weight: 3
                });
                featureGroupRef.current.addLayer(simplePolygon);
                
                // Guardar referencia al polígono editable
                if (polygonIndex === 0) {
                    polygonRef.current = simplePolygon;
                }
                
                // Guardar referencia a todos los polígonos editables
                allEditablePolygons.push({
                    index: polygonIndex,
                    polygon: simplePolygon,
                    type: 'simple'
                });
                
                // Activamos la edición estándar del polígono
                if (simplePolygon && (simplePolygon as any).editing) {
                    (simplePolygon as any).editing.enable();
                }
            }
        }
    
        // Activamos la edición estándar del polígono principal (si existe)
        if (polygonRef.current && (polygonRef.current as any).editing) {
            (polygonRef.current as any).editing.enable();
        }

        (polygonRef.current as any)._allEditablePolygons = allEditablePolygons;
        
        // Habilitar la visualización clara de los agujeros para facilitar su edición
        const currentLatLngs = polygonRef.current.getLatLngs();

        if (Array.isArray(currentLatLngs) && currentLatLngs.length > 1) {
            
            // Aseguramos que todos los anillos sean editables
            try {
                // @ts-ignore - Acceso a propiedades internas de Leaflet
                const editTools = (polygonRef.current as any).editing._poly;
                if (editTools && editTools._map) {
                }
            } catch (e) {
                console.error("Error al inicializar herramientas de edición:", e);
            }
        }
        
        // Manejador para nuevos polígonos (agujeros o multipolígonos)
        const onDrawCreated = function(e: any) {
            const layer = e.layer;
            
            // Si es un marcador, manejar la lógica de location
            if (layer && layer instanceof L.Marker) {
                if (!drawMarkerControlRef.current) {
                    return;
                }
                
                const latlng = layer.getLatLng();
                setCenter([latlng.lat, latlng.lng]);
                setIsDrawingMarker(false);
                
                // Limpiar el control de marcador
                if (drawMarkerControlRef.current) {
                    drawMarkerControlRef.current.disable();
                    drawMarkerControlRef.current = null;
                }
                if ((map as any)._activeDrawMarkerControl) {
                    delete (map as any)._activeDrawMarkerControl;
                }
                return;
            }
            
            // Si no es un polígono, salir
            if (!layer || !(layer instanceof L.Polygon) || !polygonRef.current) return;

            // Obtener coordenadas del nuevo polígono dibujado
            const newPolyCoords = layer.getLatLngs()[0];
            
            // Convertir a formato esperado
            const newPoints: L.LatLngExpression[] = [];
            try {
                // Necesitamos asegurarnos de manejar correctamente los tipos de Leaflet
                const coordsArray = Array.isArray(newPolyCoords) ? newPolyCoords : [newPolyCoords];
                for (let i = 0; i < coordsArray.length; i++) {
                    const point = coordsArray[i] as any; // Cast para acceder a lat/lng
                    if (point && typeof point.lat === 'number' && typeof point.lng === 'number') {
                        newPoints.push([point.lat, point.lng]);
                    }
                }
                
                // Validar que el polígono es válido (al menos 3 puntos)
                if (newPoints.length < 3) {
                    throw new Error("El polígono debe tener al menos 3 puntos");
                }
                
            } catch (error) {
                console.error("Error procesando coordenadas del polígono:", error);
                if (layer) {
                    map.removeLayer(layer);
                }
                return;
            }
            
            try {
                let isHole = false;
                let parentPolygon: any = null;
                const currentEditPolygons = (polygonRef.current as any)._allEditablePolygons;
                
                // Si no está en el polígono principal, verificar los multipolígonos
                for (const multiPoly of currentEditPolygons) {
                    if (!multiPoly) break;
                    
                    let outerRing: L.LatLngExpression[] = [];
                    if (multiPoly.polygon && Array.isArray(multiPoly.polygon.getLatLngs())) {
                        // Polígono con múltiples anillos - tomar el primero como exterior
                        outerRing = multiPoly.polygon.getLatLngs()[0] as L.LatLngExpression[];
                    } else {
                        // Polígono simple
                        outerRing = multiPoly as L.LatLngExpression[];
                    }
                    
                    // Verificar si el nuevo polígono está completamente dentro de este multipolígono
                    if (isPolygonInside(newPoints, outerRing)) {
                        // Convertir newPoints a L.LatLng si es necesario
                        const formattedPoints = newPoints.map(pt => {
                            if (Array.isArray(pt) && pt.length === 2) {
                                return L.latLng(pt[0], pt[1]);
                            }
                            const latLng = L.latLng(pt);
                            if (latLng && typeof latLng.lat === 'number' && typeof latLng.lng === 'number') {
                                return latLng;
                            }
                            return pt; // fallback
                        });
                        // Obtener los anillos actuales del multipolígono
                        const rings = multiPoly.polygon.getLatLngs();
                        rings.push(formattedPoints); // Agregar el nuevo agujero como anillo interior
                        multiPoly.polygon.setLatLngs(rings); // Actualizar el multipolígono con el nuevo anillo
                        isHole = true;
                        parentPolygon = multiPoly.polygon; // Guardar referencia al polígono padre
                        break;
                    }
                }

                if (isHole) {
                    const holePolygon = L.polygon(newPoints, {
                        color: 'blue',
                        weight: 2,
                        fillColor: 'blue',
                        fillOpacity: 0.2,
                        dashArray: '5, 5',
                        interactive: true
                    });
                    
                    // Lo añadimos al feature group para que sea editable
                    if (featureGroupRef.current) {
                        featureGroupRef.current.addLayer(holePolygon);
                    } else {
                        map.addLayer(holePolygon);
                    }
                    
                    // Habilitar edición para el nuevo polígono de agujero
                    if (holePolygon && (holePolygon as any).editing) {
                        (holePolygon as any).editing.enable();
                    }
                    
                    // CRÍTICO: Agregar el nuevo agujero al array _holePolygons del polígono padre,caso contrario se pierde al eliminar
                    if (parentPolygon) {
                        if (!parentPolygon._holePolygons) {
                            parentPolygon._holePolygons = [];
                        }
                        const newIndex = parentPolygon.getLatLngs().length - 1; // Índice del nuevo anillo
                        parentPolygon._holePolygons.push({
                            index: newIndex,
                            polygon: holePolygon
                        });
                    }
                    
                } else {
                    // Crear un nuevo polígono separado para el multipolígono
                    const newMultiPolygon = L.polygon(newPoints, {
                        color: 'green',
                        weight: 3,
                        fillColor: 'green',
                        fillOpacity: 0.3,
                        interactive: true
                    });
                    // Añadirlo al feature group para que sea editable
                    if (featureGroupRef.current) {
                        featureGroupRef.current.addLayer(newMultiPolygon);
                    } 

                    // Habilitar edición para el nuevo polígono
                    if (newMultiPolygon && (newMultiPolygon as any).editing) {
                        (newMultiPolygon as any).editing.enable();
                    }
                    const nextIndex = currentEditPolygons.length; // Nuevo índice para el multipolígono 
                    currentEditPolygons.push(
                        {
                            index: nextIndex,
                            polygon: newMultiPolygon,
                            type: 'new'
                        }
                    );
                }

                setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, isDrawing: false, mapEditMode: true}}));
            } catch (error) {
                console.error("Error al procesar el nuevo polígono:", error);
                map.removeLayer(layer);
            }
            // Actualizar shapeCoordinates desde _allEditablePolygons
            if (polygonRef.current && (polygonRef.current as any)._allEditablePolygons){
                const newShapeCoordinates: L.LatLngExpression[][][] = updateShapeCoordinatesFromPolygons(polygonRef);
                setShapeCoordinates(newShapeCoordinates);
            };
        };

        // Manejador específico para eliminación de agujeros y polígonos en modo edición
        const onDeleted = function(e: any) {
            const layers = e.layers;

            if (layers.getLayers().length < 1) return;

            const currentEditPolygons = (polygonRef.current as any)._allEditablePolygons;

            // Verificar qué fue eliminado
            layers.eachLayer(function(layer: any) {
                for (const poly of currentEditPolygons) {
                    if (layer === poly.polygon) { // main polygon
                        // Remover del array de polígonos editables
                        const index = currentEditPolygons.indexOf(poly);
                        if (index > -1) {
                            currentEditPolygons.splice(index, 1);
                        }

                        if (poly.polygon._holePolygons) {
                            // Si el polígono eliminado tiene agujeros, también eliminarlos del mapa y del FeatureGroup
                            for (const holeObj of poly.polygon._holePolygons) {
                                if (holeObj.polygon) {
                                    if (featureGroupRef.current) {
                                        featureGroupRef.current.removeLayer(holeObj.polygon);
                                    } else {
                                        map.removeLayer(holeObj.polygon);
                                    }
                                }
                            }
                            poly.polygon._holePolygons = [];
                        }
                        break;
                    }
                    if (poly.polygon._holePolygons) {
                        const holeIndex = poly.polygon._holePolygons.findIndex((holeObj: any) => holeObj.polygon === layer);
                        if (holeIndex > -1) {
                            // Remover del array de agujeros
                            poly.polygon._holePolygons.splice(holeIndex, 1);

                            const latLngs = poly.polygon.getLatLngs();
                            if (Array.isArray(latLngs) && latLngs.length > 1) {
                                // Eliminar el anillo correspondiente al agujero
                                latLngs.splice(holeIndex + 1, 1); // +1 porque el 0 es el exterior
                                poly.polygon.setLatLngs(latLngs);
                            }
                            break;
                        }
                    }
                }
            });

            // Actualizar shapeCoordinates desde _allEditablePolygons
            if (polygonRef.current && (polygonRef.current as any)._allEditablePolygons){
                const newShapeCoordinates: L.LatLngExpression[][][] = updateShapeCoordinatesFromPolygons(polygonRef);
                setShapeCoordinates(newShapeCoordinates);
            };
        };
        
        // Registrar manejadores de eventos
        map.on('draw:created', onDrawCreated);
        map.on('draw:deleted', onDeleted);
        
        // Guardar referencia para limpieza
        (polygonRef.current as any)._drawEventListeners = {
            created: onDrawCreated,
            deleted: onDeleted
        };

    };

    const updateShapeCoordinatesFromPolygons = (polygonRef: React.RefObject<L.Polygon | null>): L.LatLngExpression[][][] => {
        const newShapeCoordinates: L.LatLngExpression[][][] = [];
        for (const poly of (polygonRef.current as any)._allEditablePolygons) {
            if (poly.polygon) {
                const polyLatLngs = poly.polygon.getLatLngs();
                if (polyLatLngs && Array.isArray(polyLatLngs) && polyLatLngs.length > 0) {
                    const normalized = normalizeLatLngs(polyLatLngs);
                    if (normalized.length > 0 && normalized[0].length > 0 && normalized[0][0].length > 0) {
                        newShapeCoordinates.push(...normalized);
                    }
                }
            }
        }
        return newShapeCoordinates;
    };

    const normalizeLatLngs = (latlngs: any): L.LatLngExpression[][][] => {        
        // Función auxiliar para convertir un punto LatLng a [lat, lng]
        const pointToArray = (point: any): [number, number] => {
            if (Array.isArray(point) && point.length >= 2) {
                return [point[0], point[1]];
            }
            if (point && typeof point.lat === 'number' && typeof point.lng === 'number') {
                return [point.lat, point.lng];
            }
            console.warn("Punto inválido:", point);
            return [0, 0];
        };

        // Caso: Array de arrays de LatLng [[LatLng, ...], [LatLng, ...]]
        // Esto representa un polígono con anillos (exterior + agujeros)
        if (latlngs[0] && Array.isArray(latlngs[0]) && latlngs[0][0] && typeof latlngs[0][0].lat === 'number') {
            return [latlngs.map((ring: any[]) => ring.map(pointToArray))];
        }

        // Caso: Array de arrays de arrays [[[LatLng, ...]], [[LatLng, ...]]]
        // Esto representa múltiples polígonos (multipolígono)
        return latlngs.map((polygon: any) => {
            if (!polygon || !Array.isArray(polygon)) {
                console.warn("Polígono inválido en multipolígono:", polygon);
                return [[]];
            }

            // Si el polígono es un array de LatLng directamente
            if (polygon[0] && typeof polygon[0].lat === 'number') {
                return [polygon.map(pointToArray)];
            }

            // Si el polígono es un array de anillos
            if (polygon[0] && Array.isArray(polygon[0])) {
                return polygon.map((ring: any) => {
                    if (!ring || !Array.isArray(ring)) {
                        console.warn("Anillo inválido:", ring);
                        return [];
                    }
                    return ring.map(pointToArray);
                });
            }

            return [[]];
        });
    };


    const handleDrawMarker = () => {
        const map = mapRef.current;
        if (!map) return;

        // Customizar los textos del tooltip
        (L as any).drawLocal.draw.handlers.marker.tooltip = {
            start: t('MAP.MARKER_TOOLTIP_START')
        };
        
        // Crear un control de dibujo solo para marcadores
        const invisibleIcon = L.divIcon({
            iconSize: [0, 0],
            iconAnchor: [0, 0]
        });
        // @ts-ignore
        const drawMarkerControl = new L.Draw.Marker(map, {
            // icon: DefaultIcon,
            icon: invisibleIcon,
            repeatMode: false
        });
        drawMarkerControlRef.current = drawMarkerControl;
        drawMarkerControl.enable();

        // Guarda la referencia si necesitas deshabilitarlo después
        (map as any)._activeDrawMarkerControl = drawMarkerControl;
    };

    const handleCancelDrawMarker = () => {
        setIsDrawingMarker(false);
        const map = mapRef.current;
        if (!map) return;

        // Si hay un control de dibujo de marcador activo, deshabilitarlo y limpiar
        if (drawMarkerControlRef.current) {
            drawMarkerControlRef.current.disable();
            drawMarkerControlRef.current = null;
        }
    };

    const handleDrawPolygon = () => {
        const map = mapRef.current;
        if (!map) return;
        setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, isDrawing: true} }));

        // Customizar los textos del tooltip
        (L as any).drawLocal.draw.handlers.polygon.tooltip = {
            start: t('MAP.DRAW_TOOLTIP_START'),
            cont: t('MAP.DRAW_TOOLTIP_CONTINUE'),
            end: t('MAP.DRAW_TOOLTIP_END')
        };
        
        // Crear un control de dibujo solo para polígonos
        // @ts-ignore
        const drawPolygonControl = new L.Draw.Polygon(map, {
            allowIntersection: false,
            showArea: true,
            drawError: {
                color: '#e1e100',
                message: '<strong>Error:</strong> Los bordes no pueden cruzarse'
            },
            shapeOptions: {
                color: 'purple',
                fillOpacity: 0.3
            }
        });
        drawPolygonControlRef.current = drawPolygonControl;
        drawPolygonControl.enable();

        // Guarda la referencia si necesitas deshabilitarlo después
        (map as any)._activeDrawControl = drawPolygonControl;
    };

    const handleCancelDraw = () => {
        setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, isDrawing: false}}));
        const map = mapRef.current;
        if (!map) return;

        // Si hay un control de dibujo activo, deshabilitarlo y limpiar la referencia
        if (drawPolygonControlRef.current) {
            drawPolygonControlRef.current.disable();
            drawPolygonControlRef.current = null;
        }
    };

    const handleDeletePolygon = () => {
        const map = mapRef.current;
        if (!map || !featureGroupRef.current) return;

        // Guardar el estado de los polígonos antes de comenzar a borrar
        setOriginalShapeBeforeDelete(JSON.parse(JSON.stringify(shapeCoordinates)));
        // Guardar el estado de _allEditablePolygons antes de borrar, incluyendo tipo y coordenadas
        if (polygonRef.current && (polygonRef.current as any)._allEditablePolygons) {
            originalAllEditablePolygonsRef.current = (polygonRef.current as any)._allEditablePolygons.map((poly: any) => {
                let coords = [];
                try {
                    coords = poly.polygon.getLatLngs();
                } catch {
                    coords = [];
                }
                return {
                    index: poly.index,
                    type: poly.type,
                    coords: JSON.parse(JSON.stringify(coords))
                };
            });
        } else {
            originalAllEditablePolygonsRef.current = [];
        }

        if(!isDeleting) setIsDeleting(true);

        // Customizar los textos del tooltip de eliminación
        (L as any).drawLocal.edit.handlers.remove.tooltip = {
            text: t('MAP.DELETE_TOOLTIP')
        };

        // Crear el control de borrado de Leaflet Draw
        // @ts-ignore
        const deleteControl = new L.EditToolbar.Delete(map, {
            featureGroup: featureGroupRef.current
        });
        deleteControl.enable();

        // Guarda la referencia si necesitas deshabilitarlo después
        (map as any)._activeDeleteControl = deleteControl;
    };

    const handleCancelDelete = () => {
        const map = mapRef.current;
        if (!map) return;

        // Si hay un control de borrado activo, deshabilitarlo y limpiar la referencia
        if ((map as any)._activeDeleteControl) {
            (map as any)._activeDeleteControl.disable();
            (map as any)._activeDeleteControl = null;
        }

        // Limpiar el featureGroup y reconstruir los polígonos editables y el array
        if (featureGroupRef.current) {
            featureGroupRef.current.clearLayers();
        }
        if (polygonRef.current && (polygonRef.current as any)._allEditablePolygons) {
            (polygonRef.current as any)._allEditablePolygons = [];
        }

        // Intentar restaurar desde originalAllEditablePolygonsRef, sino usar fallback
        const sourceData = (originalAllEditablePolygonsRef.current &&
            Array.isArray(originalAllEditablePolygonsRef.current) &&
            originalAllEditablePolygonsRef.current.length > 0)
            ? originalAllEditablePolygonsRef.current
            : originalShapeBeforeDelete.map((coords, idx) => ({
                index: idx,
                type: 'main',
                coords
            }));

        if (!polygonRef.current || !sourceData || sourceData.length === 0) {
            setIsDeleting(false);
            return;
        }

        const allEditablePolygons: any[] = [];
        const restoredCoords: any[] = [];

        for (const polyInfo of sourceData) {
            if (!polyInfo || !polyInfo.coords || polyInfo.coords.length === 0) continue;

            const polygonCoords = polyInfo.coords;

            // Polígono con múltiples anillos (agujeros)
            if (Array.isArray(polygonCoords[0]) && polygonCoords.length > 1) {
                const mainPolygon = L.polygon(polygonCoords, {
                    color: polyInfo.type === 'new' ? 'green' : 'red',
                    fillRule: 'evenodd',
                    fillOpacity: 0.5,
                    interactive: true,
                    weight: 3
                });
                featureGroupRef.current?.addLayer(mainPolygon);
                // Habilitar edición
                if ((mainPolygon as any).editing) {
                    (mainPolygon as any).editing.enable();
                }
                allEditablePolygons.push({
                    index: polyInfo.index,
                    polygon: mainPolygon,
                    type: polyInfo.type
                });

                // Crear polígonos auxiliares para agujeros
                (mainPolygon as any)._holePolygons = [];
                for (let i = 1; i < polygonCoords.length; i++) {
                    const hole = polygonCoords[i];
                    const holePolygon = L.polygon(hole, {
                        color: 'blue',
                        weight: 2,
                        fillColor: 'blue',
                        fillOpacity: 0.2,
                        dashArray: '5, 5',
                        interactive: true
                    });
                    featureGroupRef.current?.addLayer(holePolygon);
                    // Habilitar edición en agujero
                    if ((holePolygon as any).editing) {
                        (holePolygon as any).editing.enable();
                    }
                    (mainPolygon as any)._holePolygons.push({
                        index: i,
                        polygon: holePolygon
                    });
                }

                restoredCoords.push(polygonCoords);
            } else {
                // Polígono simple
                const simplePolygon = L.polygon(polygonCoords, {
                    color: polyInfo.type === 'new' ? 'green' : 'red',
                    fillOpacity: 0.5,
                    weight: 3
                });
                featureGroupRef.current?.addLayer(simplePolygon);
                // Habilitar edición
                if ((simplePolygon as any).editing) {
                    (simplePolygon as any).editing.enable();
                }
                allEditablePolygons.push({
                    index: polyInfo.index,
                    polygon: simplePolygon,
                    type: polyInfo.type
                });

                restoredCoords.push([polygonCoords]);
            }
        }

        (polygonRef.current as any)._allEditablePolygons = allEditablePolygons;
        polygonRef.current.setLatLngs(restoredCoords as any);
        setShapeCoordinates(JSON.parse(JSON.stringify(restoredCoords)));
        setIsDeleting(false);
    };

    const handleConfirmDelete = () => {
        const map = mapRef.current;
        if (!map || !featureGroupRef.current) return;

        // Accede al control de borrado activo
        const deleteControl = (map as any)._activeDeleteControl;
        if (
            polygonRef.current &&
            (polygonRef.current as any)._drawEventListeners &&
            (polygonRef.current as any)._drawEventListeners.deleted &&
            deleteControl &&
            deleteControl._deletedLayers // Aquí Leaflet guarda las capas seleccionadas para borrar
        ) {
            const layersToDelete = deleteControl._deletedLayers.getLayers();
            const fakeEvent = {
                layers: {
                    getLayers: () => layersToDelete,
                    eachLayer: (cb: any) => layersToDelete.forEach(cb),
                }
            };
            (polygonRef.current as any)._drawEventListeners.deleted(fakeEvent);
        }
        // Si hay un control de borrado activo, deshabilitarlo y limpiar la referencia
        if ((map as any)._activeDeleteControl) {
            (map as any)._activeDeleteControl.disable();
            (map as any)._activeDeleteControl = null;
            setIsDeleting(false);
        }
    };

    const cleanTools = () => {
        const map = mapRef.current;
        if (!map) return;

        // Si hay un control de dibujo de polígono activo, deshabilitarlo y limpiar la referencia
        if ((map as any)._activeDrawControl) {
            (map as any)._activeDrawControl.disable();
            (map as any)._activeDrawControl = null;
            setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, isDrawing: false}}));
        }

        // Si hay un control de borrado activo, deshabilitarlo y limpiar la referencia
        if ((map as any)._activeDeleteControl) {
            (map as any)._activeDeleteControl.disable();
            (map as any)._activeDeleteControl = null;
            setIsDeleting(false);
        }

        // Si hay un control de dibujo de marcador activo, deshabilitarlo y limpiar la referencia
        if ((map as any)._activeDrawMarkerControl) {
            (map as any)._activeDrawMarkerControl.disable();
            (map as any)._activeDrawMarkerControl = null;
            setIsDrawingMarker(false);
        }

        if (polygonRef.current) {
            // Desactivamos la edición
            if ((polygonRef.current as any).editing) {
                (polygonRef.current as any).editing.disable();
            }
            // Limpiamos las capas auxiliares de polígonos de agujeros
            if ((polygonRef.current as any)._holePolygons) {
                const holePolygons = (polygonRef.current as any)._holePolygons;
                for (const holeObj of holePolygons) {
                    if (holeObj.polygon) {
                        // Deshabilitar edición si está activa
                        if ((holeObj.polygon as any).editing && (holeObj.polygon as any).editing._enabled) {
                            (holeObj.polygon as any).editing.disable();
                        }
                        // Eliminar del FeatureGroup y del mapa
                        if (featureGroupRef.current) {
                            featureGroupRef.current.removeLayer(holeObj.polygon);
                        } else {
                            map.removeLayer(holeObj.polygon);
                        }
                    }
                }
                delete (polygonRef.current as any)._holePolygons;
            }
            // Limpiamos las capas auxiliares de multipolígonos
            if ((polygonRef.current as any)._multiPolygons) {
                const multiPolygons = (polygonRef.current as any)._multiPolygons;
                for (const multiPoly of multiPolygons) {
                    if (multiPoly) {
                        // Deshabilitar edición si está activa
                        if ((multiPoly as any).editing && (multiPoly as any).editing._enabled) {
                            (multiPoly as any).editing.disable();
                        }
                        // Eliminar del FeatureGroup y del mapa
                        if (featureGroupRef.current) {
                            featureGroupRef.current.removeLayer(multiPoly);
                        } else {
                            map.removeLayer(multiPoly);
                        }
                    }
                }
                delete (polygonRef.current as any)._multiPolygons;
            }
            // También limpiar las líneas auxiliares por si acaso quedan del enfoque anterior
            if ((polygonRef.current as any)._holeLines) {
                const holeLines = (polygonRef.current as any)._holeLines;
                for (const holeLine of holeLines) {
                    map.removeLayer(holeLine);
                }
                delete (polygonRef.current as any)._holeLines;
            }
            // Limpiamos listeners y controles específicos para edición de agujeros
            if ((polygonRef.current as any)._drawEventListeners) {
                const listeners = (polygonRef.current as any)._drawEventListeners;
                if (listeners.created) map.off('draw:created', listeners.created);
                if (listeners.editvertex) map.off('draw:editvertex', listeners.editvertex);
                if (listeners.deleted) map.off('draw:deleted', listeners.deleted);
                delete (polygonRef.current as any)._drawEventListeners;
            }
            if ((polygonRef.current as any)._editHoleControl) {
                map.removeControl((polygonRef.current as any)._editHoleControl);
                delete (polygonRef.current as any)._editHoleControl;
            }
            // Restaurar la forma original
            polygonRef.current.setLatLngs(originalShape);
        }
    };

    // Función para guardar cambios
    const handleSaveEdit = async () => {
        if (onPolygonEdit) {
            await onPolygonEdit(editNewShape, editNewLocation);
        }
        
        cleanTools();
        setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, mapEditMode: false, isEditing: false} }));
        closeConfirmEdit();
    };

    const openCancelEdit = () => {
        if (JSON.stringify(originalShape) === JSON.stringify(shapeCoordinates)) {
            // No hay cambios, cerrar directamente la edición
            handleCancelEdit();
            return;
        }
        setCancelEdit(true);
    };

    const closeCancelEdit = () => {
        setCancelEdit(false);
    };

    const closeConfirmEdit = () => {
        setConfirmEdit(false);
    };

    const openConfirmEdit = () => {
        setConfirmEdit(true);

        if (!polygonRef.current) {
            return;
        }

        const map = mapRef.current;
        if (!map) return;

        const allEditablePolygons = (polygonRef.current as any)._allEditablePolygons;
        let multipolyStr = "(";
        for (const polyGroup of allEditablePolygons) {
            // Filtrar solo anillos con puntos
            const latLngsArr = (polyGroup.polygon.getLatLngs() as L.LatLngExpression[][][]);
            // Puede ser array de arrays (multipolígonos) o array simple (polígono)
            const ringsArr = Array.isArray(latLngsArr[0]) ? latLngsArr : [latLngsArr as any];
            let groupStr = "";
            for (const poly of ringsArr) {
                if (!poly || poly.length === 0) continue;
                const processedPoints = poly.map((point: any) => `${point.lng} ${point.lat}`);
                // Aseguramos que el polígono esté cerrado
                if (processedPoints.length > 0 && processedPoints[0] !== processedPoints[processedPoints.length - 1]) {
                    processedPoints.push(processedPoints[0]);
                }
                if (processedPoints.length > 0) {
                    groupStr += `(${processedPoints.join(", ")}), `;
                }
            }
            if (groupStr.endsWith(", ")) {
                groupStr = groupStr.slice(0, -2);
            }
            if (groupStr.length > 0) {
                multipolyStr += `(${groupStr}), `;
            }
        }
        if (multipolyStr.endsWith(", ")) {
            multipolyStr = multipolyStr.slice(0, -2);
        }
        multipolyStr += ")";

        const newShape = multipolyStr && multipolyStr !== "()" ? `MULTIPOLYGON ${multipolyStr}` : null;
        const finalShape = newShape ? "SRID=4326;" + newShape : null;

        // Guardar el nuevo location (center) en la UP
        let newLocation = 'SRID=4326;POINT ';
        if (center && Array.isArray(center) && center.length === 2) {
            newLocation += `(${center[1]} ${center[0]})`;
        } else if (center && typeof center === 'object' && 'lat' in center && 'lng' in center) {
            newLocation += `(${center.lng} ${center.lat})`;
        } else {
            newLocation = getCentroidFromShapeWKT(newShape || "");
        }

        setEditNewShape(finalShape);
        setEditNewLocation(newLocation);

    }

    // Función para cancelar edición
    const handleCancelEdit = () => {
        
        if (polygonRef.current) {
            cleanTools();

            // Restaurar la forma original
            polygonRef.current.setLatLngs(originalShape);
        }
        
        setShapeCoordinates(JSON.parse(JSON.stringify(originalShape))); // Copia profunda para evitar referencias
        setCenter(originalCenter); // Restaurar el centro original
        setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, mapEditMode: false, isEditing: false} }));
        closeCancelEdit();
    };


    return (
        <Box ref={containerRef} sx={{minHeight: 220, height: '100%', width: "100%"}}>
            <MapContainer 
                id="mapLeaflet" 
                center={center}
                zoom={14} 
                style={{minHeight: 220, width: "100%",height: "100%"}}
                scrollWheelZoom={true}
                doubleClickZoom={false} 
                zoomControl={false}
            >  
                {/* Handlers */}
                <SetMapRef mapRef={mapRef} />
                <MapCenterHandler containerRef={containerRef} isEditing={sharedData.mapViewerPage.isEditing} shapeCoordinates={shapeCoordinates} center={center}/>

                <TileLayer 
                    {...tileProps}
                />
                <CustomZoomControl shapeCoordinates={shapeCoordinates} location={center} />
                {/* Botones flotantes personalizados */}
                <Box sx={{
                    position: 'absolute',
                    top: 180,
                    right: 15,
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    { sharedData.mapViewerPage.isEditing && (
                        <>
                            <Box sx={{ display: "flex", flexDirection: "flow", gap: 1 }}>
                                <IconButton data-testid="draw-button" onClick={() => (setSharedData(prev => ({...prev, mapViewerPage: {...prev.mapViewerPage, isDrawing: true}})))} title={t('COMMON.DRAW')} sx={styles.floatIconButton} disabled={sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker}>
                                    <PentagonOutlinedIcon sx={{ fontSize: "24px", color: sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker ? theme.palette.neutral_2 : theme.palette.neutral_0 }} />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: "flex", flexDirection: "flow", gap: 1 }}>
                                <IconButton data-testid="delete-button" onClick={handleDeletePolygon} sx={styles.floatIconButton} disabled={sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker}>
                                    <SvgIcon inheritViewBox component={IconLib.eraserSvg} title={<Trans>COMMON.DELETE</Trans>} sx={{ fontSize: "24px", color: sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker ? theme.palette.neutral_2 : theme.palette.neutral_0, '& path, & line, & polyline, & circle, & rect': { strokeWidth: 4 } }} />
                                </IconButton>
                            </Box>
                            {mode === "create" &&
                                <Box sx={{ display: "flex", flexDirection: "flow", gap: 1 }}>
                                    <IconButton data-testid="define-location-button" onClick={() => setIsDrawingMarker(true)} title={t('MAP.DEFINE_LOCATION')} sx={styles.floatIconButton} disabled={sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker}>
                                        <SvgIcon inheritViewBox component={IconLib.addPinSvg} title={<Trans>MAP.ADD_LOCATION</Trans>} sx={{ fontSize: "24px", color: sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker ? theme.palette.neutral_2 : theme.palette.neutral_0, '& path, & line, & polyline, & circle, & rect': { strokeWidth: 4 } }} />
                                    </IconButton>
                                </Box>
                            }
                            <Box sx={{ position: 'fixed', bottom: 50, right: 40, zIndex: 1200 }}>
                                <Box sx={[styles.tabs]}>
                                    <ButtonApp variant="text" color="secondary" data-testid="cancel-button" sx={[styles.tabButton, { color: theme.palette.neutral_0 }]} onClick={openCancelEdit}>
                                        <Typography variant="button" sx={[{ color: theme.palette.neutral_0 }]}><Trans>COMMON.CANCEL</Trans></Typography>
                                    </ButtonApp>
                                    <Typography variant="h5" sx={{ color: theme.palette.neutral_3 }}>|</Typography>
                                    <ButtonApp variant="text" color="secondary" data-testid="openConfirmEdit-button" sx={[styles.tabButton, { color: theme.palette.neutral_0 }]} onClick={openConfirmEdit} disabled={(sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker || !canEditPolygon)}>
                                        <Typography variant="button" sx={[{ color: (sharedData.mapViewerPage.isDrawing || isDeleting || isDrawingMarker || !canEditPolygon) ? theme.palette.neutral_3 : theme.palette.neutral_0 }]}><Trans>COMMON.FINISH</Trans></Typography>
                                    </ButtonApp>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
                { sharedData.mapViewerPage.isDrawing &&
                    <Box sx={{position: 'absolute', bottom: 50, left: 40, zIndex: 1200}}>
                        <Box sx={[styles.tabs]}>
                            <PentagonOutlinedIcon sx={{ fontSize: "20px", color: theme.palette.tertiary.main}} />
                            <ButtonApp variant="text" color="secondary" data-testid="cancel-draw-button" sx={[styles.tabButton, {color: theme.palette.neutral_0}]} onClick={handleCancelDraw}>
                                <Typography variant="button" sx={[{color: theme.palette.neutral_1}]}><Trans>MAP.CANCEL_DRAWING</Trans></Typography>
                            </ButtonApp>
                        </Box>
                    </Box>
                }
                { isDrawingMarker &&
                    <Box 
                        sx={{position: 'absolute', bottom: 50, left: 40, zIndex: 1200}}
                        onMouseEnter={() => {
                            // Deshabilitar temporalmente el control de marcador
                            if (drawMarkerControlRef.current) {
                                drawMarkerControlRef.current.disable();
                            }
                        }}
                        onMouseLeave={() => {
                            // Re-habilitar el control de marcador si seguimos en modo definición
                            if (isDrawingMarker && drawMarkerControlRef.current) {
                                drawMarkerControlRef.current.enable();
                            }
                        }}
                    >
                        <Box sx={[styles.tabs]}>
                            <SvgIcon inheritViewBox component={IconLib.addPinSvg} title={<Trans>MAP.ADD_LOCATION</Trans>} sx={{ fontSize: "20px", color: theme.palette.tertiary.main, '& path, & line, & polyline, & circle, & rect': { strokeWidth: 4 } }} />
                            <ButtonApp variant="text" color="secondary" data-testid="cancel-marker-button" sx={[styles.tabButton, {color: theme.palette.neutral_0}]} onClick={handleCancelDrawMarker}>
                                <Typography variant="button" sx={[{color: theme.palette.neutral_1}]}><Trans>MAP.CANCEL_MARKER</Trans></Typography>
                            </ButtonApp>
                        </Box>
                    </Box>
                }
                { isDeleting &&
                    <Box sx={{position: 'absolute', bottom: 50, left: 40, zIndex: 1200}}>
                        <Box sx={[styles.tabs]}>
                            <SvgIcon inheritViewBox component={IconLib.eraserSvg} sx={{ fontSize: "20px", color: theme.palette.tertiary.main, '& path, & line, & polyline, & circle, & rect': {strokeWidth: 4 }}} />
                            <ButtonApp variant="text" color="secondary" data-testid="cancel-delete-button" sx={[styles.tabButton, {color: theme.palette.neutral_0}]} onClick={handleCancelDelete}>
                                <Typography variant="button" sx={[{color: theme.palette.neutral_1}]}><Trans>MAP.CANCEL_DELETE</Trans></Typography>
                            </ButtonApp>
                            <Typography variant="h5" sx={{color: theme.palette.neutral_3}}>|</Typography>
                            <ButtonApp variant="text" color="secondary" data-testid="confirm-delete-button" sx={[styles.tabButton,{color: theme.palette.neutral_0}]} onClick={handleConfirmDelete}>
                                <Typography variant="button" sx={[{color: theme.palette.neutral_1}]}><Trans>MAP.CONFIRM_DELETE</Trans></Typography>
                            </ButtonApp>
                        </Box>
                    </Box>
                }
                { indexImageOverlay?.image &&
                    <ImageOverlay url={`data:image/png;base64,${indexImageOverlay.image}`} bounds={indexImageOverlay.bbox as L.LatLngBoundsExpression}/>
                }
                { miniMapImage?.image &&
                    <MiniMap indexImageOverlay={miniMapImage} tileProps={tileProps} />
                }
                { geoJsonList && Array.isArray(geoJsonList) && geoJsonList.length > 0 &&
                    <GeoJsonLayer geoJsonList={geoJsonList} />
                }
                { shapeCoordinates.length > 0 && shapeCoordinates[0].length > 0 && !sharedData.mapViewerPage.isEditing &&
                    <Polygon
                        positions={shapeCoordinates} 
                        pathOptions={{ 
                            color: 'red',
                            fillRule: 'evenodd', // Importante para renderizar correctamente los anillos interiores
                            fillOpacity: sharedData.mapViewerPage.isEditing ? 0.5 : 0
                        }} 
                    />
                }
                { showLocation && center && !sharedData.mapViewerPage.isEditing &&
                    <Marker position={center} icon={DefaultIcon} />
                }
                {  center && sharedData.mapViewerPage.isEditing && !isDrawingMarker &&
                    <Marker
                        position={center}
                        icon={DefaultIcon}
                        draggable={true}
                        eventHandlers={{
                            dragend: (e: any) => {
                                const latlng = e.target.getLatLng();
                                setCenter([latlng.lat, latlng.lng]);
                            },
                        }}
                    />
                }
            </MapContainer>
            <ConfirmationDialog open={confirmEdit} onClose={closeConfirmEdit} onConfirm={handleSaveEdit}
                title={t( !canConfirmEdit ? "MAP.TITLE_CANT_EDIT_POLYGON" : "COMMON.CONFIRMATION")} subtitle={t(!canConfirmEdit ? "MAP.MSJ_CANT_EDIT_POLYGON" : "MAP.MSJ_CONF_EDIT")} disabled={!canConfirmEdit}/>
            <ConfirmationDialog open={cancelEdit} onClose={closeCancelEdit} onConfirm={handleCancelEdit}
                title={t( mode === "create" ? "MAP.TITLE_CANCEL_CREATION" : "MAP.TITLE_CANCEL_EDIT")} subtitle={t(mode === "create" ? "MAP.MSJ_CANCEL_CREATION" : "MAP.MSJ_CANCEL_EDIT")}/>
        </Box>
    )
}
